import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { Select } from "components/form_components/Inputs";
import { useMyFeedPageState } from "components/my_feed/useMyFeedPageState";
import { generateNoticeSearchUrl } from "components/notices/utils";
import { StrategyPaywallContent } from "components/paywall/strategy_paywall/StrategyPaywallContent";
import Button from "components/ui/button/Button";
import { useSubscription } from "lib/hooks/subscription";
import { useURLState } from "lib/hooks/useURLState";
import { EventNames, useTracking } from "lib/tracking";
import {
  NoticeAnalysisByGroupGroupBy,
  NoticeAnalysisByPeriodGroupBy,
  NoticeAnalysisByPeriodInterval,
} from "lib/types/graphQLEnums";
import ByGroup from "./ByGroup";
import ByPeriod from "./ByPeriod";

type NoticeAnalysisForm = {
  groupBy: NoticeAnalysisByGroupGroupBy | NoticeAnalysisByPeriodGroupBy;
  interval: NoticeAnalysisByPeriodInterval;
  chartShow: "noticeCount" | "noticeValues";
};

const DEFAULT_VALUES: NoticeAnalysisForm = {
  groupBy: NoticeAnalysisByGroupGroupBy.Buyer,
  interval: NoticeAnalysisByPeriodInterval.Month, // only shown if groupBy is for a time period
  chartShow: "noticeCount", // only shown if groupBy is for a time period
};

function NoticeAnalysisContent() {
  const { logEvent } = useTracking();
  const subscription = useSubscription();

  const {
    filters: noticeViewFilters,
    setFilters: setNoticeViewFilters,
    selectedView,
    isLoadingViews,
  } = useMyFeedPageState(true);
  const [analysisFilters = DEFAULT_VALUES, setAnalysisFilters] = useURLState<
    NoticeAnalysisForm | undefined
  >("analysis", DEFAULT_VALUES);

  const { control, watch, handleSubmit } = useForm<NoticeAnalysisForm>({
    mode: "onChange",
    defaultValues: analysisFilters,
  });

  // Set up watch subscription to set state when form data passes validation
  useEffect(() => {
    // decided to not update period filters (interval & measure) when groupBy changes between buyer/supplier & period charts
    // this conserves previous user choice for period filters
    const onSubmit = handleSubmit((data) => setAnalysisFilters(data));

    const subscription = watch(() => onSubmit());
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, logEvent, setAnalysisFilters]);

  useEffect(() => {
    const isPeriod = byPeriod(analysisFilters);

    logEvent(EventNames.marketAnalysisFilterChanged, {
      grouping: analysisFilters.groupBy,
      measure: isPeriod ? analysisFilters.chartShow : "N/A",
      interval: isPeriod ? analysisFilters.interval : "N/A",
    });
  }, [analysisFilters, logEvent]);

  if (!subscription.hasDataTypes("MARKET_ANALYSIS")) {
    return (
      <PaywallContainer>
        <StrategyPaywallContent
          featureType={"STRATEGY"}
          trackingData={{ "Page source": "Notice Analysis - Paywall" }}
        />
      </PaywallContainer>
    );
  }

  if (isLoadingViews) {
    // TODO: Update to look nicer
    return <div>Loading...</div>;
  }

  return (
    <Flex column width="100%" height="100vh">
      <BreadcrumbContainer>
        <Flex gap="8px" alignItems="center">
          <Button
            icon="searchRefraction"
            buttonStyle="inverse"
            as="a"
            href={generateNoticeSearchUrl(noticeViewFilters, selectedView?.id)}
          >
            {selectedView?.name}
          </Button>
          /
          <CurrentBreadcrumb buttonStyle="inverse" icon="barChart12">
            Analysis
          </CurrentBreadcrumb>
        </Flex>
        {/* <Button icon="share07">
          Share
          </Button> */}
      </BreadcrumbContainer>
      <Flex flexGrow={1}>
        <ContentWrapper>
          {byPeriod(analysisFilters) ? (
            <ByPeriod
              filters={noticeViewFilters}
              groupBy={analysisFilters.groupBy as NoticeAnalysisByPeriodGroupBy}
              interval={analysisFilters.interval}
              chartShow={analysisFilters.chartShow}
              setFilters={setNoticeViewFilters}
              selectedViewId={selectedView?.id ?? undefined}
            />
          ) : (
            <ByGroup
              filters={noticeViewFilters}
              groupBy={analysisFilters.groupBy as NoticeAnalysisByGroupGroupBy}
              setFilters={setNoticeViewFilters}
              selectedViewId={selectedView?.id ?? undefined}
            />
          )}
        </ContentWrapper>
        <SidebarContainer column gap={10}>
          <Text h2>Trend analysis</Text>
          <form>
            <Select
              name="groupBy"
              label="Grouped by"
              control={control}
              options={[
                { label: "Buyers", value: NoticeAnalysisByGroupGroupBy.Buyer },
                { label: "Suppliers", value: NoticeAnalysisByGroupGroupBy.Supplier },
                {
                  label: "Published date",
                  value: NoticeAnalysisByPeriodGroupBy.PublishDate,
                },
                {
                  label: "Expiry date",
                  value: NoticeAnalysisByPeriodGroupBy.ExpiryDate,
                },
                {
                  label: "Award date",
                  value: NoticeAnalysisByPeriodGroupBy.AwardDate,
                },
              ]}
              optionFilterProp="label"
            />
            {byPeriod(analysisFilters) && (
              <>
                <Select
                  name="chartShow"
                  label="Measure"
                  control={control}
                  options={[
                    { label: "Notice count", value: "noticeCount" },
                    { label: "Total value", value: "noticeValues" },
                  ]}
                  optionFilterProp="label"
                />
                <Select
                  name="interval"
                  label="Interval"
                  control={control}
                  options={[
                    { label: "Monthly", value: NoticeAnalysisByPeriodInterval.Month },
                    { label: "Quarterly", value: NoticeAnalysisByPeriodInterval.Quarter },
                    { label: "Yearly", value: NoticeAnalysisByPeriodInterval.Year },
                  ]}
                  optionFilterProp="label"
                />
              </>
            )}
          </form>
        </SidebarContainer>
      </Flex>
    </Flex>
  );
}

const BreadcrumbContainer = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px",
  borderBottom: `1px solid ${theme.colors.sysBorderPrimary}`,
}));

const ContentWrapper = styled(Flex)(({ theme }) => ({
  padding: "24px",
  borderRight: `1px solid ${theme.colors.sysBorderPrimary}`,
  height: "100%",
  width: "100%",
  maxWidth: "calc(100% - 360px)",
}));

const SidebarContainer = styled(Flex)(({ theme }) => ({
  width: "360px",
  padding: "24px",
  borderBottom: `1px solid ${theme.colors.sysBorderPrimary}`,
}));

const PaywallContainer = styled(Flex)({
  height: "80%",
});

const CurrentBreadcrumb = styled(Button)({
  "&:hover": {
    cursor: "default",
    background: "none",
  },
});

const byPeriod: (form: NoticeAnalysisForm) => boolean = (form) => {
  return Object.values(NoticeAnalysisByPeriodGroupBy).includes(
    form.groupBy as NoticeAnalysisByPeriodGroupBy,
  );
};
export default NoticeAnalysisContent;
